'use client';

import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { Button, InputField, Modal, ModalContent } from '@pickleballinc/react-ui';
import { RecaptchaNotice } from '@pickleballinc/ui/components/ui/recaptcha-notice';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useRecaptchaEnterprise } from '@/hooks/useRecaptchaEnterprise';
import { RECAPTCHA_SHORT_ERROR_MESSAGE } from '@/lib/recaptcha/constants';
import { validatePassword } from '@/utils/actions/apr/validatePassword';
import { CaptchaAction } from '@/utils/helpers/captcha-actions';
import { getAPRPath } from '@/utils/helpers/getAPRPath';

interface PasswordModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const PasswordModal = ({ open, onOpenChange }: PasswordModalProps) => {
  const router = useRouter();
  const { executeRecaptcha } = useRecaptchaEnterprise();
  const [password, setPassword] = useState('');
  const [displayError, setDisplayError] = useState('');
  const [isPasswordHidden, setIsPasswordHidden] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (value: string) => {
    setPassword(value);
    setDisplayError('');
  };

  const handleContinue = async () => {
    setIsLoading(true);
    setDisplayError('');

    const captchaToken = await executeRecaptcha(CaptchaAction.APR_PASSWORD);

    if (!captchaToken) {
      setDisplayError(RECAPTCHA_SHORT_ERROR_MESSAGE);
      setIsLoading(false);
      return;
    }

    const result = await validatePassword({
      password,
      captchaToken,
      captchaAction: CaptchaAction.APR_PASSWORD,
    });

    if (result?.error) {
      setDisplayError(RECAPTCHA_SHORT_ERROR_MESSAGE);
      setIsLoading(false);
      return;
    }

    if (!result?.data) {
      setDisplayError('Incorrect password');
      setIsLoading(false);
      return;
    }

    router.refresh();
    router.push(getAPRPath(''));
    setTimeout(() => setIsLoading(false), 3000);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className='flex w-[calc(100%-16px)] max-w-[400px] flex-col gap-6 !overflow-hidden !p-6 !outline-none'>
        <div className='flex flex-col w-full gap-1.5'>
          <span className='text-sm text-gray-700 font-medium'>
            {formatText({
              text: 'Password',
              role: TextRole.InputLabel,
            })}
          </span>
          <InputField
            className='!rounded-lg px-1 !h-10 shadow-xs'
            type={isPasswordHidden ? 'password' : 'text'}
            value={password}
            SuffixIcon={() => (
              <Button
                variant='link'
                type='button'
                aria-label={isPasswordHidden ? 'Show password' : 'Hide password'}
                onClick={() => setIsPasswordHidden((prev) => !prev)}
                className='flex items-center justify-center text-gray-500 hover:text-gray-700'
              >
                <FontAwesomeIcon size='sm' icon={isPasswordHidden ? faEyeSlash : faEye} />
              </Button>
            )}
            hasError={!!displayError}
            placeholder={formatText({
              text: 'Enter password',
              role: TextRole.Placeholder,
            })}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleContinue();
              }
            }}
          />
          {displayError ? (
            <span className='text-error-500 text-sm'>
              {formatText({
                text: displayError,
                role: TextRole.ErrorMessage,
              })}
            </span>
          ) : null}
          <RecaptchaNotice className='text-gray-900 text-xs' />
        </div>

        <Button
          variant='primary'
          onClick={handleContinue}
          size='lg'
          disabled={isLoading}
          className='flex text-lg justify-center'
        >
          {formatText({
            text: isLoading ? 'Logging in...' : 'Log in',
            role: TextRole.ButtonLabel,
          })}
        </Button>
      </ModalContent>
    </Modal>
  );
};
